import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  listModerationPosts,
  moderatePost,
  getPostDetail,
} from "../../Service/moderationApi";
import "./moderation.css";

const profileUrl = (hccId) => `https://indiacastingclub.com/profile/${hccId}`;
const postUrl = (uid) => `https://indiacastingclub.com/p/${uid}`;

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PostCard = ({ row, onPreview, onAction }) => {
  const status = row?.moderation_status || "notreviewed";
  return (
    <article className={`moderation-post-card moderation-card--${status}`}>
      <div
        className="moderation-post-preview"
        role="button"
        tabIndex={0}
        onClick={() => onPreview(row)}
        onKeyDown={(e) => e.key === "Enter" && onPreview(row)}
      >
        <div className="moderation-post-preview-placeholder">
          {row?.media_type === "video" ? "Video post" : "Image post"}
          <br />
          Click to preview
        </div>
      </div>
      <div className="moderation-post-body">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <span className="moderation-id">#{row?.id}</span>
          <span className={`moderation-status-badge ${status}`}>{status}</span>
          {row?.media_type ? (
            <span className="moderation-type-badge">{row.media_type}</span>
          ) : null}
        </div>
        <div className="moderation-post-caption">{row?.caption || "No caption"}</div>
        <div className="moderation-meta-strip mb-2">
          <div className="moderation-meta-item">
            <label>Owner</label>
            <span>
              {row?.profile_name || "--"}
              {row?.profile_hcc_id ? ` (${row.profile_hcc_id})` : ""}
            </span>
          </div>
          <div className="moderation-meta-item">
            <label>Created</label>
            <span>{formatDateTime(row?.created_at || row?.cdate)}</span>
          </div>
        </div>
        <div className="moderation-post-actions">
          {row?.profile_hcc_id ? (
            <a
              href={profileUrl(row.profile_hcc_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-primary btn-sm"
              onClick={(e) => e.stopPropagation()}
            >
              Profile
            </a>
          ) : null}
          {row?.uid ? (
            <a
              href={postUrl(row.uid)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary btn-sm"
              onClick={(e) => e.stopPropagation()}
            >
              Public link
            </a>
          ) : null}
          {status !== "approved" ? (
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={() => onAction(row, "approve")}
            >
              Approve
            </button>
          ) : null}
          {status !== "rejected" ? (
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={() => onAction(row, "reject")}
            >
              Reject
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => onAction(row, "delete")}
          >
            Delete
          </button>
        </div>
      </div>
    </article>
  );
};

const PostReviewQueue = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(20);
  const [moderationStatus, setModerationStatus] = useState("notreviewed");
  const [hccIdFilter, setHccIdFilter] = useState("");
  const [hccId, setHccId] = useState("");

  const [showActionModal, setShowActionModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");

  const getData = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: rowPerPage };
      if (moderationStatus) params.moderation_status = moderationStatus;
      if (hccId) params.hcc_id = hccId;

      const response = await listModerationPosts(params);
      setRows(response?.data?.results || []);
      setTotalRows(response?.data?.count || 0);
    } catch (error) {
      console.error(error);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [currentPage, rowPerPage, moderationStatus, hccId]);

  const openPreview = async (row) => {
    setSelected(row);
    setPreviewData(null);
    setShowPreviewModal(true);
    setPreviewLoading(true);
    try {
      const res = await getPostDetail(row.id);
      setPreviewData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openAction = (row, action) => {
    setSelected(row);
    setActionType(action);
    setActionNote(row?.moderation_note || "");
    setActionError("");
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    if (!selected?.id || !actionType) return;
    if (actionType === "delete" && !window.confirm("Soft-delete this post? It will be removed from all views.")) {
      return;
    }
    setActionLoading(true);
    setActionError("");
    try {
      await moderatePost(selected.id, actionType, actionNote.trim());
      setShowActionModal(false);
      setSelected(null);
      setActionType("");
      setActionNote("");
      getData();
    } catch (error) {
      const data = error?.response?.data;
      setActionError(data?.detail || data?.action?.[0] || "Action failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleHccSearch = (e) => {
    e.preventDefault();
    setHccId(hccIdFilter.trim());
    setCurrentPage(1);
  };

  const actionLabels = {
    approve: "Approve post",
    reject: "Reject post",
    delete: "Delete post",
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / rowPerPage));
  const rangeStart = totalRows === 0 ? 0 : (currentPage - 1) * rowPerPage + 1;
  const rangeEnd = Math.min(currentPage * rowPerPage, totalRows);

  const previewMedia = previewData || selected;
  const isVideo = previewMedia?.media_type === "video" || !!previewMedia?.video_url;

  return (
    <div className="child-sidenav">
      <div className="childnav-content user-screen moderation-page">
        <div className="moderation-toolbar">
          <div className="btn-group" role="group" aria-label="moderation status">
            <button
              type="button"
              className={`btn btn-sm ${moderationStatus === "notreviewed" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => { setModerationStatus("notreviewed"); setCurrentPage(1); }}
            >
              Queue
            </button>
            <button
              type="button"
              className={`btn btn-sm ${moderationStatus === "approved" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => { setModerationStatus("approved"); setCurrentPage(1); }}
            >
              Approved
            </button>
            <button
              type="button"
              className={`btn btn-sm ${moderationStatus === "rejected" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => { setModerationStatus("rejected"); setCurrentPage(1); }}
            >
              Rejected
            </button>
            <button
              type="button"
              className={`btn btn-sm ${moderationStatus === "" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => { setModerationStatus(""); setCurrentPage(1); }}
            >
              All
            </button>
          </div>

          <form className="moderation-search" onSubmit={handleHccSearch}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filter by HCC ID"
              value={hccIdFilter}
              onChange={(e) => setHccIdFilter(e.target.value)}
            />
            <button type="submit" className="btn btn-sm btn-outline-secondary">Filter</button>
            {hccId ? (
              <button
                type="button"
                className="btn btn-sm btn-link"
                onClick={() => { setHccId(""); setHccIdFilter(""); setCurrentPage(1); }}
              >
                Clear
              </button>
            ) : null}
          </form>

          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={getData}>
            Refresh
          </button>

          <div className="moderation-toolbar-summary">
            <b>{totalRows}</b> posts
          </div>
        </div>

        {loading ? (
          <div className="moderation-loader">
            <img src={require("../../Components/FormParser/images/loader2.gif")} alt="Loading..." />
          </div>
        ) : rows.length === 0 ? (
          <div className="moderation-empty">No posts in this queue.</div>
        ) : (
          <div className="moderation-post-grid">
            {rows.map((row) => (
              <PostCard key={row.id} row={row} onPreview={openPreview} onAction={openAction} />
            ))}
          </div>
        )}

        {totalRows > 0 && (
          <div className="moderation-pagination">
            <div>
              Rows per page:{" "}
              <select
                value={rowPerPage}
                onChange={(e) => { setRowPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {[10, 20, 25, 30, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>{rangeStart}-{rangeEnd} of {totalRows}</div>
            <div className="moderation-pagination-controls">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </button>
              <span>Page {currentPage} / {totalPages}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal centered size="lg" show={showPreviewModal} onHide={() => setShowPreviewModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Post #{selected?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewLoading ? (
            <div className="text-center py-4">Loading preview...</div>
          ) : (
            <>
              <div className="moderation-post-preview mb-3" style={{ maxHeight: 400 }}>
                {isVideo && previewMedia?.video_url ? (
                  <video src={previewMedia.video_url} controls poster={previewMedia.thumbnail || previewMedia.image} />
                ) : previewMedia?.image ? (
                  <img src={previewMedia.image} alt={previewMedia?.caption || "Post"} />
                ) : (
                  <div className="moderation-post-preview-placeholder">No media available</div>
                )}
              </div>
              <p>{previewMedia?.caption || "No caption"}</p>
              <div className="moderation-meta-strip">
                <div className="moderation-meta-item">
                  <label>Owner</label>
                  <span>{selected?.profile_name} ({selected?.profile_hcc_id})</span>
                </div>
                <div className="moderation-meta-item">
                  <label>Status</label>
                  <span>{selected?.moderation_status || "--"}</span>
                </div>
              </div>
              <div className="moderation-action-row mt-3">
                {selected?.moderation_status !== "approved" ? (
                  <button type="button" className="btn btn-success btn-sm" onClick={() => { setShowPreviewModal(false); openAction(selected, "approve"); }}>
                    Approve
                  </button>
                ) : null}
                {selected?.moderation_status !== "rejected" ? (
                  <button type="button" className="btn btn-warning btn-sm" onClick={() => { setShowPreviewModal(false); openAction(selected, "reject"); }}>
                    Reject
                  </button>
                ) : null}
                <button type="button" className="btn btn-danger btn-sm" onClick={() => { setShowPreviewModal(false); openAction(selected, "delete"); }}>
                  Delete
                </button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal centered show={showActionModal} onHide={() => !actionLoading && setShowActionModal(false)}>
        <Modal.Header closeButton={!actionLoading}>
          <Modal.Title>{actionLabels[actionType] || "Moderate post"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected ? (
            <>
              <p className="mb-2">
                Post #{selected.id} · {selected.profile_hcc_id || "--"}
              </p>
              <label className="form-label"><b>Note</b> (optional)</label>
              <textarea
                className="form-control"
                rows={3}
                value={actionNote}
                disabled={actionLoading}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder="Moderation note..."
              />
              {actionError ? (
                <div className="alert alert-danger py-2 mt-2">{actionError}</div>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" disabled={actionLoading} onClick={() => setShowActionModal(false)}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${actionType === "delete" ? "btn-danger" : actionType === "reject" ? "btn-warning" : "btn-success"}`}
            disabled={!selected || actionLoading}
            onClick={confirmAction}
          >
            {actionLoading ? "Processing..." : "Confirm"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export { PostReviewQueue };
